import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { Visible } from "react-grid-system";
import styled from "styled-components";
import { theme } from "../../theme";
import {
  ChevronRightIconLarge,
  ChevronLeftIconLarge,
} from "../atoms/icons/ChevronIcons";
import { useTranslation } from "react-i18next";
import BodySmall from "../atoms/typography/BodySmall";

interface Props {
  tabs: Array<{ id: string; name: string }>;
  selectedTabId: string;
  onSelectTab: (tabId: string) => void;
}

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
`;

const Container = styled.div`
  padding: 4px;
  border-radius: 8px;
  background-color: white;
  display: flex;
  align-items: center;
  box-shadow: ${theme.shadow.cardShadow};
  flex-wrap: wrap;

  @media (max-width: 768px) {
    overflow-x: auto;
    white-space: nowrap;
    scrollbar-width: none;
    -ms-overflow-style: none;
    position: relative;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const ScrollButton = styled.div<{ $position: "left" | "right" }>`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 40px;
  ${({ $position }) =>
    $position === "left" ? "left: -15px;" : "right: -15px;"}
  background: ${({ $position }) =>
    $position === "left"
      ? "linear-gradient(to right, rgba(251, 251, 251, 1), rgba(251, 251, 251, 0.2))"
      : "linear-gradient(to left, rgba(251, 251, 251, 1), rgba(251, 251, 251, 0.2))"};
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Tab = styled.button<{ selected: boolean }>`
  border: none;
  background-color: ${(props) =>
    props.selected ? theme.colors.grey05 : "transparent"};
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${theme.colors.grey10};
  }
`;

const TabBar: React.FC<Props> = ({ tabs, selectedTabId, onSelectTab }) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);

  const handleScroll = (direction: "left" | "right") => {
    if (containerRef.current) {
      const scrollAmount = direction === "right" ? 140 : -140;
      containerRef.current.scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const updateScrollButtons = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft + clientWidth < scrollWidth);
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      updateScrollButtons();
      container.addEventListener("scroll", updateScrollButtons);
      return () => container.removeEventListener("scroll", updateScrollButtons);
    }
  }, []);

  return (
    <Wrapper>
      <Visible xs sm>
        {showLeftScroll && (
          <ScrollButton
            $position="left"
            onClick={() => handleScroll("left")}
            aria-label="Scroll left"
          >
            <ChevronLeftIconLarge color={theme.colors.textSecondary} />
          </ScrollButton>
        )}
      </Visible>
      <Container ref={containerRef}>
        {tabs.map((tab) => (
          <Tab
            key={tab.id}
            selected={tab.id === selectedTabId}
            onClick={() => onSelectTab(tab.id)}
          >
            <BodySmall
              fontweight={500}
              color={
                tab.id === selectedTabId
                  ? theme.colors.primary
                  : theme.colors.textSecondary
              }
            >
              {t(tab.name)}
            </BodySmall>
          </Tab>
        ))}
      </Container>
      <Visible xs sm>
        {showRightScroll && (
          <ScrollButton
            $position="right"
            onClick={() => handleScroll("right")}
            aria-label="Scroll right"
          >
            <ChevronRightIconLarge color={theme.colors.textSecondary} />
          </ScrollButton>
        )}
      </Visible>
    </Wrapper>
  );
};

export default TabBar;
